import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, Lock, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function LoadingGrid({ count = 6, className }) {
  return (
    <div data-testid="loading-state" className={cn("grid gap-5 sm:grid-cols-2 lg:grid-cols-3", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="lm-card space-y-4 p-5">
          <div className="flex gap-4">
            <Skeleton className="h-12 w-12 rounded-2xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          <Skeleton className="h-3 w-2/3" />
          <div className="flex gap-2"><Skeleton className="h-5 w-16 rounded-full" /><Skeleton className="h-5 w-20 rounded-full" /></div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon = SearchX, title = "Aucun résultat", description, action, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="lm-card flex flex-col items-center px-6 py-16 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="text-lg font-bold">{title}</h3>
      {description && <p className="mt-1.5 max-w-md text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({ message = "Le chargement a échoué. Réessayez dans quelques instants.", onRetry }) {
  return (
    <div data-testid="error-state" className="lm-card flex flex-col items-center px-6 py-14 text-center">
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
        <AlertTriangle className="h-6 w-6" />
      </span>
      <h3 className="text-lg font-bold">Oups, un problème est survenu</h3>
      <p className="mt-1.5 max-w-md text-sm text-muted-foreground">{message}</p>
      {onRetry && <Button variant="outline" className="mt-6 rounded-full" onClick={onRetry} data-testid="error-retry-button">Réessayer</Button>}
    </div>
  );
}

export function LoginPrompt({ title = "Fonctionnalité réservée aux membres de La Mêlée", description, compact = false }) {
  const { pathname, search } = useLocation();
  const next = encodeURIComponent(pathname + search);
  return (
    <div data-testid="login-prompt" className={cn("lm-card flex flex-col items-start gap-4 border-dashed sm:flex-row sm:items-center", compact ? "p-5" : "p-8")}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-primary"><Lock className="h-5 w-5" /></span>
      <div className="flex-1">
        <h3 className="font-bold">{title}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {description || "Connectez-vous avec votre adresse @lamelee.com pour accéder aux informations internes."}
        </p>
      </div>
      <Button asChild className="rounded-full px-5" data-testid="login-prompt-button">
        <Link to={`/connexion?next=${next}`}>Se connecter</Link>
      </Button>
    </div>
  );
}
